/**
 * Local persistence for the POS terminal.
 *
 * Everything here is DEVICE state, not user state: which device this terminal
 * is bound to, which mode it was activated in, which station board it shows,
 * and the tokens of whoever is currently signed in on it. It survives a reload
 * (a KDS screen that logs itself out after a power blip is useless) but is
 * wiped on logout and on device revocation.
 *
 * `localStorage` rather than IndexedDB because these are five small scalars
 * read synchronously during the very first render — an async read would flash
 * the login screen at an already-signed-in terminal.
 */

import type { AuthTokens } from '@sonauto/api-client';

const PREFIX = 'sonauto.pos.';

const KEYS = {
  tokens: `${PREFIX}tokens`,
  deviceId: `${PREFIX}deviceId`,
  deviceMode: `${PREFIX}deviceMode`,
  deviceName: `${PREFIX}deviceName`,
  branchId: `${PREFIX}branchId`,
  stationId: `${PREFIX}stationId`,
  cashSessionId: `${PREFIX}cashSessionId`,
  tenantCode: `${PREFIX}tenantCode`,
} as const;

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private mode / storage disabled. The app still works, it just forgets.
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore — see read() */
  }
}

export function loadTokens(): AuthTokens | null {
  const raw = read(KEYS.tokens);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return null;
    const t = parsed as Partial<AuthTokens>;
    if (typeof t.accessToken !== 'string' || typeof t.refreshToken !== 'string' || typeof t.expiresAt !== 'number') {
      return null;
    }
    return { accessToken: t.accessToken, refreshToken: t.refreshToken, expiresAt: t.expiresAt };
  } catch {
    return null;
  }
}

export function saveTokens(tokens: AuthTokens | null): void {
  write(KEYS.tokens, tokens === null ? null : JSON.stringify(tokens));
}

export interface DeviceBinding {
  readonly deviceId: string;
  readonly deviceName: string;
  readonly branchId: string | null;
  /**
   * The mode chosen at activation. Stored because the API does NOT return
   * `device_mode` to a POS session (`/auth/me` carries only `deviceId`, and
   * `GET /devices` needs `device.view` which a cashier does not hold).
   */
  readonly deviceMode: string | null;
  readonly stationId: string | null;
}

export function loadDeviceBinding(): DeviceBinding | null {
  const deviceId = read(KEYS.deviceId);
  if (deviceId === null || deviceId === '') return null;
  return {
    deviceId,
    deviceName: read(KEYS.deviceName) ?? 'Thiết bị POS',
    branchId: read(KEYS.branchId),
    deviceMode: read(KEYS.deviceMode),
    stationId: read(KEYS.stationId),
  };
}

export function saveDeviceBinding(binding: DeviceBinding | null): void {
  if (binding === null) {
    write(KEYS.deviceId, null);
    write(KEYS.deviceName, null);
    write(KEYS.branchId, null);
    write(KEYS.deviceMode, null);
    write(KEYS.stationId, null);
    return;
  }
  write(KEYS.deviceId, binding.deviceId);
  write(KEYS.deviceName, binding.deviceName);
  write(KEYS.branchId, binding.branchId);
  write(KEYS.deviceMode, binding.deviceMode);
  write(KEYS.stationId, binding.stationId);
}

/** The till this terminal has open, so a reload does not orphan a cash session. */
export function loadCashSessionId(): string | null {
  return read(KEYS.cashSessionId);
}

export function saveCashSessionId(id: string | null): void {
  write(KEYS.cashSessionId, id);
}

/** Last tenant code typed at the login screen — a terminal never changes tenant. */
export function loadTenantCode(): string {
  return read(KEYS.tenantCode) ?? '';
}

export function saveTenantCode(code: string): void {
  write(KEYS.tenantCode, code);
}

/**
 * A stable-ish fingerprint for device activation. Deliberately NOT a tracking
 * id: it is coarse, it is hashed server-side with `PII_HASH_KEY`, and it exists
 * only so a revoked terminal can be told apart from a fresh one.
 */
export function deviceFingerprint(): string {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  const parts = [
    nav?.userAgent ?? 'unknown',
    nav?.language ?? '',
    String(typeof screen === 'undefined' ? '' : `${screen.width}x${screen.height}`),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ];
  return parts.join('|').slice(0, 200);
}
